import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { videoPreviewAPI } from '../../services/api';
import { VideoPlayerEnhanced } from '../player/VideoPlayerEnhanced';
import { logger } from '../../utils/logger';

interface VideoPreviewProps {
  conversationId: string;
  onBackToGallery: () => void;
  className?: string;
}

export const VideoPreview: React.FC<VideoPreviewProps> = ({
  conversationId,
  onBackToGallery,
  className = ""
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [manifestExists, setManifestExists] = useState(false);

  useEffect(() => {
    const checkManifest = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if manifest.json exists
        const manifestUrl = videoPreviewAPI.getJSONUrl(conversationId, 'manifest.json');
        const manifestResponse = await fetch(manifestUrl);

        if (!manifestResponse.ok) {
          throw new Error('Manifest.json not found. Please complete video generation first.');
        }

        setManifestExists(true);
        logger.debug('✅ Manifest found for conversation:', conversationId);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load video player');
        setManifestExists(false);
      } finally {
        setLoading(false);
      }
    };

    checkManifest();
  }, [conversationId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-muted-foreground">Loading video player...</div>
      </div>
    );
  }

  if (error || !manifestExists) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-lg text-destructive">
          {error || 'Video not available'}
        </div>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          Complete the video generation workflow to preview your video presentation.
        </p>
        <Button onClick={onBackToGallery} variant="outline">
          Back to Gallery
        </Button>
      </div>
    );
  }

  return (
    <VideoPlayerEnhanced
      conversationId={conversationId}
      onBackToGallery={onBackToGallery}
      className={className}
    />
  );
};
